import React from 'react';
import { Box, Typography } from '@mui/material';
import PublicPage from './PublicPage';
import { PRIVACY_NOTICE_VERSION } from './waitlistCopy';

/**
 * Where the notice's text names the contact address. The address itself is put together only when
 * the page renders (contactAddress), so it never sits whole in the bundle for harvesters to find.
 */
const CONTACT = '{contact}';
const CONTACT_PARTS = ['privacy', 'dwlf.co.uk'];
export const contactAddress = () => CONTACT_PARTS.join(String.fromCharCode(64));
const MONO = '"IBM Plex Mono", monospace';

/**
 * /privacy (DWLF-584): the UK GDPR notice the waitlist's consent line links to. Its text is part of
 * what a person consents to: change PRIVACY_NOTICE_VERSION with any change here (privacyNoticeVersion.test.js holds it to that).
 */
const SECTIONS = [
  {
    heading: 'Who we are',
    body: [
      'DWLF Ltd ("DWLF", "we") is a company registered in England and Wales, company number 17466266, registered office 128 City Road, London EC1V 2NX. We are the controller of the personal data this notice describes.',
      `To contact us about your data, email ${CONTACT}. Mail sent there reaches DWLF's founder and is used only to answer privacy requests.`,
    ],
  },
  {
    heading: 'What this notice covers',
    body: ['The DWLF waitlist: the form on dwlf.co.uk where you can ask to be told when DWLF opens to new members.'],
  },
  {
    heading: 'What we collect',
    body: [
      'Your email address.',
      'The interests you tick, if any (stocks, crypto, metals, AI agents).',
      'Where on our site you joined (for example the home page or the sign-in page), the version of this notice you agreed to, and when you joined.',
      'If you tried to sign in with Google first, we fill in the email address Google gave us. We only keep it if you submit the form.',
      'We do not store your IP address. To stop the form being abused, we keep a one-way scrambled (hashed) form of it for up to two hours, then delete it.',
    ],
  },
  {
    heading: 'Why we use it, and our legal basis',
    body: [
      'To email you when DWLF opens to new members, and to see which markets people are most interested in, so we know what to open first. Our legal basis is your consent (UK GDPR Article 6(1)(a)).',
      'The hashed network address is kept to protect the form from abuse. Our legal basis is our legitimate interest in keeping the waitlist working (Article 6(1)(f)).',
      "We won't send you marketing or newsletters from this list, and we won't sell your data.",
    ],
  },
  {
    heading: 'Who else handles it',
    body: [
      "Amazon Web Services (AWS) hosts our systems and stores the waitlist in its London region. When we email you that access is open, the email is sent through AWS too. AWS acts only on our instructions.",
      "If you tried to sign in with Google, Google's own privacy policy covers what Google does with your data.",
      'We share your data with no one else.',
    ],
  },
  {
    heading: 'How long we keep it',
    body: [
      "Until 12 months after we've told you DWLF is open, or until you ask us to remove you, whichever comes first. If DWLF hasn't opened within 24 months of your joining, we delete your entry.",
    ],
  },
  {
    heading: 'Your rights',
    body: [
      `You can withdraw your consent at any time. Email ${CONTACT} and we'll take you off the list. Every email we send you will also include a way to unsubscribe. Withdrawing doesn't affect what we did before you withdrew.`,
      `You can also ask us for a copy of your data, to correct it, or to delete it. Email ${CONTACT} and we'll respond within one month.`,
    ],
  },
  {
    heading: 'Complaints',
    body: [
      "If you're unhappy with how we've handled your data, please tell us first. You can also complain to the Information Commissioner's Office (ICO) at ico.org.uk/make-a-complaint or on 0303 123 1113.",
    ],
  },
  {
    heading: 'Changes to this notice',
    body: ['If we change this notice, we give it a new version, shown at the top. If a change affects how we use your data, we will ask for your consent again.'],
  },
];

/** The notice exactly as a person reads it: what they consent to, and what the version guard fingerprints. */
export const sectionsAsShown = () => SECTIONS.map(({ heading, body }) => ({ heading, body: body.map((p) => p.replaceAll(CONTACT, contactAddress())) }));

export default function PrivacyNotice() {
  return (
    <PublicPage maxWidth={720}>
      <Typography component="h1" sx={{ fontFamily: '"Space Grotesk", sans-serif', fontWeight: 500, fontSize: { xs: '2rem', md: '2.5rem' }, letterSpacing: '-0.02em', mb: 1 }}>
        Privacy notice
      </Typography>
      <Typography sx={{ fontFamily: MONO, fontSize: '0.8rem', color: 'var(--dwlf-ink-50)', mb: 4 }}>
        DWLF waitlist · version {PRIVACY_NOTICE_VERSION}
      </Typography>
      {sectionsAsShown().map(({ heading, body }) => (
        <Box component="section" key={heading} sx={{ mb: 3 }}>
          <Typography component="h2" sx={{ fontFamily: '"Space Grotesk", sans-serif', fontSize: '1.25rem', fontWeight: 500, mb: 1 }}>
            {heading}
          </Typography>
          {body.map((p) => (
            <Typography key={p} sx={{ fontFamily: MONO, fontSize: '0.9rem', lineHeight: 1.7, color: 'var(--dwlf-ink-70)', mb: 1 }}>
              {p}
            </Typography>
          ))}
        </Box>
      ))}
    </PublicPage>
  );
}
